import React, { useMemo } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import {
  computeTplProjectMatrix,
  templateMatrixFlags,
  type TplMatrixGroupStat,
} from '../../services/tplProjectMatrixInsights';
import { LST_FREQUENCY_LABELS } from '../../types';
import type { LstTemplate } from '../../types';
import { formatTimeHHmm } from '../../services/lstMatrixHourly';

interface TplProjectMatrixPanelProps {
  templates: LstTemplate[];
  weekDates: string[];
  selectedTemplateId: string | null;
  onSelectTemplate: (template: LstTemplate) => void;
  onDeactivateTemplate: (id: string) => void;
}

/** Matriz TPL — visão de construção do sistema de gestão. */
export const TplProjectMatrixPanel: React.FC<TplProjectMatrixPanelProps> = ({
  templates,
  weekDates,
  selectedTemplateId,
  onSelectTemplate,
  onDeactivateTemplate,
}) => {
  const matrix = useMemo(
    () => computeTplProjectMatrix(templates, weekDates),
    [templates, weekDates],
  );

  const { header, conflictingIds } = matrix;
  const hasHighlights =
    header.criticalCount > 0 ||
    header.withoutIndicatorCount > 0 ||
    header.withoutCategoryCount > 0 ||
    header.conflictingCount > 0;

  return (
    <div className="flex min-h-0 flex-col">
      <div className="shrink-0 border-b border-slate-100 px-2 py-2 dark:border-slate-800">
        <p className="text-[10px] font-bold uppercase tracking-wide text-[#2F6FED]">Matriz TPL</p>
        {hasHighlights && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {header.criticalCount > 0 && (
              <HighlightChip tone="red" label={`${header.criticalCount} crítica(s)`} />
            )}
            {header.withoutIndicatorCount > 0 && (
              <HighlightChip tone="amber" label={`${header.withoutIndicatorCount} sem indicador`} />
            )}
            {header.withoutCategoryCount > 0 && (
              <HighlightChip tone="amber" label={`${header.withoutCategoryCount} sem categoria`} />
            )}
            {header.conflictingCount > 0 && (
              <HighlightChip tone="red" label={`${header.conflictingCount} conflitante(s)`} />
            )}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {matrix.groups.length === 0 ? (
          <p className="px-2 py-8 text-center text-[12px] text-slate-400">
            Nenhuma rotina no escopo. Adicione rotinas para montar a matriz TPL.
          </p>
        ) : (
          matrix.groups.map((group) => (
            <MatrixGroup
              key={group.key}
              group={group}
              selectedTemplateId={selectedTemplateId}
              conflictingIds={conflictingIds}
              onSelect={onSelectTemplate}
              onDeactivate={onDeactivateTemplate}
            />
          ))
        )}
      </div>
    </div>
  );
};

function MatrixGroup({
  group,
  selectedTemplateId,
  conflictingIds,
  onSelect,
  onDeactivate,
}: {
  group: TplMatrixGroupStat;
  selectedTemplateId: string | null;
  conflictingIds: Set<string>;
  onSelect: (t: LstTemplate) => void;
  onDeactivate: (id: string) => void;
}) {
  const [open, setOpen] = React.useState(true);

  return (
    <div className="mb-2 rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-slate-900/40"
      >
        {open ? <ChevronDown size={12} className="text-slate-400" /> : <ChevronRight size={12} className="text-slate-400" />}
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold text-slate-800 dark:text-slate-100">{group.label}</p>
          <p className="text-[9px] tabular-nums text-slate-500">
            {group.count} rotina(s) · {group.minutesWeek} min · {group.pctAgenda}% da agenda
          </p>
        </div>
      </button>

      {open && (
        <ul className="border-t border-slate-100 px-1 py-1 dark:border-slate-800">
          {group.templates.map((t) => {
            const flags = templateMatrixFlags(t, conflictingIds);
            const selected = selectedTemplateId === t.id;
            const warn = flags.critical || flags.noIndicator || flags.noCategory || flags.conflicting;
            return (
              <li
                key={t.id}
                className={`group mb-0.5 flex items-center gap-1 rounded-md border px-1.5 py-1 ${
                  selected
                    ? 'border-blue-200 bg-blue-50/80 dark:border-blue-800 dark:bg-blue-950/30'
                    : warn
                      ? 'border-amber-200/80 bg-amber-50/30 dark:border-amber-900/40 dark:bg-amber-950/10'
                      : 'border-transparent hover:border-slate-200 dark:hover:border-slate-700'
                }`}
              >
                <button type="button" onClick={() => onSelect(t)} className="min-w-0 flex-1 text-left">
                  <div className="flex items-center gap-1">
                    {flags.conflicting && <AlertTriangle size={9} className="shrink-0 text-red-500" />}
                    <span className="truncate text-[11px] font-semibold text-slate-800 dark:text-slate-100">
                      {t.title}
                    </span>
                    {flags.critical && (
                      <span className="shrink-0 rounded bg-red-100 px-0.5 text-[7px] font-bold text-red-600">
                        C
                      </span>
                    )}
                  </div>
                  <p className="text-[9px] text-slate-400">
                    {LST_FREQUENCY_LABELS[t.frequency]}
                    {t.scheduled_time && ` · ${formatTimeHHmm(t.scheduled_time) ?? t.scheduled_time}`}
                    {t.duration_minutes ? ` · ${t.duration_minutes}min` : ''}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => onSelect(t)}
                  className="rounded p-0.5 text-slate-400 opacity-0 group-hover:opacity-100 hover:text-slate-600"
                >
                  <Pencil size={11} />
                </button>
                <button
                  type="button"
                  onClick={() => onDeactivate(t.id)}
                  className="rounded p-0.5 text-slate-400 opacity-0 group-hover:opacity-100 hover:text-red-600"
                >
                  <Trash2 size={11} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function HighlightChip({ label, tone }: { label: string; tone: 'red' | 'amber' }) {
  const cls =
    tone === 'red'
      ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
      : 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300';
  return (
    <span className={`rounded px-1.5 py-0.5 text-[8px] font-bold uppercase ${cls}`}>{label}</span>
  );
}

export default TplProjectMatrixPanel;
